/**
 * CameraCapture - Opens camera stream and captures a photo
 * Works on desktop (webcam), mobile web (iOS Safari / Android Chrome),
 * and native Capacitor (iOS / Android) via the native Camera plugin.
 */
import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Camera, X, RotateCcw } from 'lucide-react';
import { isNative } from '@/utils/capacitor';

const CameraCapture = ({ open, onOpenChange, onCapture }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');

  // Detect iOS (both native and Safari)
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // On native platforms, use the Capacitor Camera plugin directly
  const captureNative = useCallback(async () => {
    try {
      const { Camera: CapCamera, CameraResultType, CameraSource } = await import('@capacitor/camera');
      const photo = await CapCamera.getPhoto({
        quality: 80,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        width: 1280,
        height: 960,
        correctOrientation: true,
      });
      if (photo.dataUrl) {
        const res = await fetch(photo.dataUrl);
        const blob = await res.blob();
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
        onCapture(file);
        onOpenChange(false);
      }
    } catch (err) {
      if (err.message?.includes('cancelled') || err.message?.includes('canceled')) {
        onOpenChange(false);
        return;
      }
      console.error('Native camera error:', err);
      setError('Kamera nicht verfügbar. Bitte Zugriff in den Einstellungen erlauben.');
    }
  }, [onCapture, onOpenChange]);

  // Start web camera with iOS-compatible constraints
  const startCamera = useCallback(async (facing) => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      setReady(false);
      setError(null);

      // iOS Safari-specific constraints:
      // - Avoid exact facingMode constraints (use ideal)
      // - Lower resolution for initial connection reliability
      // - Must call play() explicitly after srcObject assignment
      const constraints = isIOS
        ? {
            video: {
              facingMode: { ideal: facing },
              width: { ideal: 1280 },
              height: { ideal: 960 },
            },
            audio: false,
          }
        : {
            video: {
              facingMode: facing,
              width: { ideal: 1280 },
              height: { ideal: 960 },
            },
            audio: false,
          };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // iOS Safari requires explicit play() call after assigning srcObject
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => setReady(true))
            .catch((playErr) => {
              console.warn('Video play error:', playErr);
              // On iOS, a second attempt after a short delay often works
              setTimeout(() => {
                videoRef.current?.play()
                  .then(() => setReady(true))
                  .catch(() => setError('Video konnte nicht gestartet werden.'));
              }, 300);
            });
        }
        videoRef.current.onloadedmetadata = () => {
          if (!ready) setReady(true);
        };
      }
    } catch (err) {
      console.error('Camera error:', err);
      if (err.name === 'NotAllowedError') {
        setError('Kamerazugriff verweigert. Bitte erlauben Sie den Zugriff in den Einstellungen.');
      } else if (err.name === 'NotFoundError' || err.name === 'NotReadableError') {
        setError('Keine Kamera gefunden oder Kamera wird bereits verwendet.');
      } else if (err.name === 'OverconstrainedError') {
        // Fallback: try without facingMode constraint
        try {
          const fallbackStream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false,
          });
          streamRef.current = fallbackStream;
          if (videoRef.current) {
            videoRef.current.srcObject = fallbackStream;
            videoRef.current.play().then(() => setReady(true)).catch(() => {});
          }
        } catch {
          setError('Kamera nicht verfügbar.');
        }
      } else {
        setError('Kamera nicht verfügbar. Bitte Zugriff erlauben.');
      }
    }
  }, [isIOS, ready]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setReady(false);
    setError(null);
  }, []);

  useEffect(() => {
    if (open) {
      if (isNative) {
        captureNative();
      } else {
        startCamera(facingMode);
      }
    } else {
      stopCamera();
    }
    return () => stopCamera();
  }, [open, facingMode, startCamera, stopCamera, captureNative]);

  const capture = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    stopCamera();

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
        onCapture(file);
        onOpenChange(false);
      }
    }, 'image/jpeg', 0.8);
  }, [onCapture, onOpenChange, stopCamera]);

  const switchCamera = () => {
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  };

  const handleClose = useCallback((value) => {
    if (!value) stopCamera();
    onOpenChange(value);
  }, [stopCamera, onOpenChange]);

  // On native platforms, the Capacitor Camera dialog handles everything
  // The Dialog below is only shown for web camera stream
  if (isNative) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden bg-black border-none" aria-describedby="camera-desc">
        <DialogTitle className="sr-only">Kamera</DialogTitle>
        <p id="camera-desc" className="sr-only">Foto aufnehmen mit der Kamera</p>
        <div className="relative">
          {error ? (
            <div className="flex flex-col items-center justify-center h-64 text-white p-6 text-center">
              <Camera className="w-12 h-12 mb-3 text-gray-400" />
              <p className="text-sm">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => startCamera(facingMode)}
                className="mt-4 text-white border-white/30"
              >
                Erneut versuchen
              </Button>
            </div>
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              webkit-playsinline="true"
              className="w-full max-h-[70vh] object-contain"
              style={{ WebkitTransform: 'translateZ(0)' }}
            />
          )}
          <canvas ref={canvasRef} className="hidden" />

          {/* Controls */}
          <div className="absolute bottom-0 left-0 right-0 p-4 flex items-center justify-center gap-6 bg-gradient-to-t from-black/80 to-transparent">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleClose(false)}
              className="text-white hover:bg-white/20 h-10 w-10"
              data-testid="camera-close-btn"
            >
              <X className="h-5 w-5" />
            </Button>

            <button
              onClick={capture}
              disabled={!ready}
              className="w-16 h-16 rounded-full border-4 border-white bg-white/20 hover:bg-white/40 disabled:opacity-30 transition-colors flex items-center justify-center"
              data-testid="camera-capture-btn"
            >
              <div className="w-12 h-12 rounded-full bg-white" />
            </button>

            <Button
              variant="ghost"
              size="icon"
              onClick={switchCamera}
              className="text-white hover:bg-white/20 h-10 w-10"
              data-testid="camera-switch-btn"
            >
              <RotateCcw className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CameraCapture;
